import angular from 'angular';

function fieldName(field) {
  return field.split(':')[1];
}

import { EnumSelector, HierarchicalEnumSelector, RadioSelector } from '.';

/**
 * Splitted from metric.controller.js, to lighten it (~2800 lines before split)
 * We can probably do better when migrating in angular, for now it's not refactoring, just splitting.
 */
export class SelectorsService {
  // useless declarations in JS (not in TS), just to see class members at a glance
  $translate;
  LocalStorageFactory;
  MetricsFactory;
  StrategiesFactory;

  constructor($translate, LocalStorageFactory, MetricsFactory, StrategiesFactory) {
    this.$translate = $translate;
    this.LocalStorageFactory = LocalStorageFactory;
    this.MetricsFactory = MetricsFactory;
    this.StrategiesFactory = StrategiesFactory;
  }

  // ------- selectors -------

  accountSelector(selector, sites, company, metric) {
    const fields = selector.fields ? selector.fields : ['where:account'];
    const name = this.$translate.instant('metrics/account');
    const items = sites.map(i => ({ id: [i.id], name: i.name, type_name: i.type_name }));

    return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.displayed_by_default
    );
  }

  actionRoutingTagsSelector(selector, sortedTagGroups, routingTagsUsesAi, company, metric) {
    const fields = selector.fields ? selector.fields : ['where:action_routing_tag'];
    const selectors = sortedTagGroups.filter(tg => tg.is_routing);
    if (selectors.length === 0) {
      return [];
    }

    return [this._tagSelector(selector, selectors[0], fields, routingTagsUsesAi, company, metric)];
  }

  // not used
  botSelector(selector, company, metric) {
    const bots = company.users.filter(u => u.is_bot === 1);
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    return this.userSelector(selector, name, bots);
  }

  customEventSelector(selector, sortedCustomEvents, company, metric) {
    if (selector.filters?.length) {
      sortedCustomEvents = sortedCustomEvents.filter(event =>
        selector.filters.some(filter => filter.values.includes(event[filter.type]))
      );
    }

    const name = this.$translate.instant('metrics/custom-event');

    return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields ? selector.fields : ['where:custom_event'],
      name,
      sortedCustomEvents,
      selector.displayed_by_default
    );
  }

  customFieldSelector(selector, company, metric) {
    if (!(metric.extra_data?.customFieldValues && metric.extra_data.customFieldValues[selector.name])) {
      return null;
    }

    const fields = selector.fields || [`where:${selector.name}`];
    const items = metric.extra_data.customFieldValues[selector.name].map(value => ({ id: value, name: value }));

    return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      selector.display_name,
      items,
      selector.displayed_by_default
    );
  }

  // not used
  enumSelector(selector, company, metric) {
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const metadata = metric.enum_metadata.filter(i => i.name === fieldName(selector.fields[0]));
    const items = metadata[0].values.map(i => {
      return {
        id: [i],
        name: this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}/${i}`),
      };
    });

    const s = new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields,
      name,
      items,
      selector.displayedByDefault,
      null
    );

    return [s];
  }

  // not used
  hoHnoSelector(selector, company, metric) {
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const items = [
      { id: 'business-hours', name: this.$translate.instant('metrics/bhnbh/bh') },
      { id: 'non-business-hours', name: this.$translate.instant('metrics/bhnbh/nbh') },
    ];

    const s = new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields,
      name,
      items,
      selector.displayed_by_default,
      { isBhNbh: true }
    );

    return [s];
  }

  // not used
  humanSelector(selector, company, metric) {
    const humans = company.users.filter(u => u.is_bot === 0);
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    return this._usersSelector(selector, name, humans, company, metric);
  }

  humanOrBotSelector(selector, company, metric) {
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const items = [
      { id: 1, name: this.$translate.instant('metrics/human_or_bot/bots_only') },
      { id: 0, name: this.$translate.instant('metrics/human_or_bot/humans_only') },
    ];
    const fields = selector.fields ? selector.fields : ['where:is_bot'];

    const s = new RadioSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.is_default,
      true
    );

    return [s];
  }

  incomingRoutingTagsSelector(selector, sortedTagGroups, routingTagsUsesAi, company, metric) {
    const fields = selector.fields ? selector.fields : ['where:incoming_team_tag'];
    const selectors = sortedTagGroups.filter(tg => tg.is_routing);
    if (selectors.length === 0) {
      return [];
    }

    return [this._tagSelector(selector, selectors[0], fields, routingTagsUsesAi, company, metric)];
  }

  linkedToCrmSelector(selector, company, metric) {
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const items = [
      { id: 'not null', name: this.$translate.instant('yes') },
      { id: 'null', name: this.$translate.instant('no') },
    ];
    const fields = selector.fields ? selector.fields : ['where:crm_link'];

    const s = new RadioSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.is_default,
      true,
      selector.displayed_by_default
    );

    return [s];
  }

  manualOrAutomaticSelector(selector, company, metric) {
    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const items = [
      { id: 1, name: this.$translate.instant('metrics/manual_or_automatic/manual') },
      { id: 0, name: this.$translate.instant('metrics/manual_or_automatic/automatic') },
    ];
    const fields = selector.fields ? selector.fields : ['where:is_manual'];

    const s = new RadioSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.is_default,
      true
    );

    return [s];
  }

  qrScenarioSelector(selector, allScenarios, sites, company, metric) {
    const items = allScenarios.map(scenario => ({
      id: scenario.quick_reply_answers,
      name: scenario.name,
      is_active_class: scenario.is_active ? 'green' : 'red',
      site_type: sites.find(s => s.id === scenario.site_id).type_name || '',
    }));

    const scenarioSelector = new RadioSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields,
      this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`),
      items,
      selector.is_default,
      false
    );

    scenarioSelector.keepColumn = function (column) {
      return angular.isUndefined(column.qrButtonId) || scenarioSelector.selected.id.indexOf(column.qrButtonId) !== -1;
    };

    return [scenarioSelector];
  }

  rolesSelector(selector, sortedRoles, company, metric) {
    const fields = ['where:action_role'];
    const name = this.$translate.instant('metrics/roles');

    return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      sortedRoles,
      selector.displayed_by_default
    );
  }

  /**
   * This selector is for scenario ids, uncompatible with statistics pages that count scenario button clicks
   * Those should use the QrScenarioSelector which send answer button_id's corresponding to the selected scenarios.
   */
  scenarioSelector(selector, flavors, sites, careScenarios, surveyScenarios, marketingScenarios, company, metric) {
    const allScenariosWithInputFlavor = [];

    for (const flavor of flavors) {
      if (flavor === 'quick-reply') {
        allScenariosWithInputFlavor.push(...careScenarios);
      } else if (flavor === 'survey') {
        allScenariosWithInputFlavor.push(...surveyScenarios);
      } else if (flavor === 'marketing') {
        allScenariosWithInputFlavor.push(...marketingScenarios);
      }
    }

    const items = allScenariosWithInputFlavor
      .sort((s1, s2) => s2.is_active - s1.is_active)
      .map(scenario => ({
        id: [scenario.id],
        name: scenario.name,
        is_active_class: scenario.is_active ? 'green' : 'red',
        site_type: sites.find(s => s.id === scenario.site_id).type_name || '',
      }));

    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);

    return [
      this._enumSelectorWithActiveStatus(selector.fields, name, items, selector.is_default, false, company, metric),
    ];
  }

  surveyScenarioSelector(selector, surveyScenarios, sites, company, metric) {
    const items = surveyScenarios.map(scenario => ({
      id: scenario.id,
      name: scenario.name,
      is_active_class: scenario.is_active ? 'green' : 'red',
      site_type: sites.find(s => s.id === scenario.site_id).type_name || '',
    }));

    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);

    const s = new RadioSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields,
      name,
      items,
      selector.is_default,
      false
    );

    return [s];
  }

  /**
   * List of all tags selectors
   */
  tagsSelectors(selector, sortedTagGroups, routingTagsUsesAi, company, metric) {
    const prefixes = selector.fields_prefixes ? selector.fields_prefixes : ['where:task_tag_'];
    const selectors = sortedTagGroups
      .map(tagGroup => {
        const fields = prefixes.map(prefix => `${prefix}${tagGroup.tag_index + 1}`);
        return this._tagSelector(selector, tagGroup, fields, routingTagsUsesAi, company, metric);
      })
      .filter(i => i !== null);
    return selectors;
  }

  /**
   * Channel
   */
  taskTypeSelector(selector, siteTypesSet, allowedTaskTypes, company, metric) {
    const name = this.$translate.instant('metrics/task_type');

    const tasksTypes = [];

    for (const siteType of siteTypesSet) {
      const strategy = this.StrategiesFactory.getStrategyFromType(siteType);

      // deleted site but account still exists
      if (strategy) {
        const filteredTaskTypes = strategy
          .getAllTasksTypesForSearchAndMetrics()
          .filter(tt => !allowedTaskTypes.length || allowedTaskTypes.includes(tt));

        tasksTypes.push({
          name: this.$translate.instant(`account_type/${strategy.getTypeString()}`),
          subitems: filteredTaskTypes.map(tt => ({
            id: [tt],
            name: this.$translate.instant(`task/${strategy.getTaskTypeString(tt)}`),
          })),
        });
      }
    }

    const s = new HierarchicalEnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      selector.fields ? selector.fields : ['where:task_type'],
      name,
      tasksTypes,
      selector.displayed_by_default
    );

    return [s];
  }

  timeToAnswerSurveySelector(selector, company, metric) {
    const name = this.$translate.instant('metric/days_to_answer_survey');
    const items = [
      { id: '1', name: this.$translate.instant('1') },
      { id: '2', name: this.$translate.instant('2') },
      { id: '5', name: this.$translate.instant('5') },
      { id: '10', name: this.$translate.instant('10') },
      { id: '30', name: this.$translate.instant('30') },
      { id: '60', name: this.$translate.instant('60') },
    ];
    const fields = ['where_timeToAnswerSurvey:time_to_answer_survey'];

    return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.displayed_by_default,
      { timeToAnswerSurvey: true }
    );
  }

  userSelector(selector, usersForStats, company, metric) {
    const items = [
      {
        name: this.$translate.instant('metrics/users/bots'),
        subitems: usersForStats.bots,
      },
      {
        name: this.$translate.instant('metrics/users/humans'),
        subitems: usersForStats.humans
      },
    ];

    const name = this.$translate.instant(`metrics/${metric.internal_name}/${selector.name}`);
    const fields = selector.fields ? selector.fields : ['where:user'];

    const s = new HierarchicalEnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.displayed_by_default
    );

    return [s];
  }

  userTeamSelector(selector, sortedTagGroups, routingTagsUsesAi, company, metric) {
    const fields = selector.fields ? selector.fields : ['where:user-team'];
    const s = sortedTagGroups.reduce((tagsSelector, tag) => {
      if (tag.is_routing) {
        const tagSelector = this._tagSelector(selector, tag, fields, routingTagsUsesAi, company, metric);

        if (tagSelector) {
          tagsSelector.push(tagSelector);
        }
      }
      return tagsSelector;
    }, []);
    return s;
  }

  // ------ others -------

  _enumSelectorWithActiveStatus(fields, name, items, displayedByDefault, config, company, metric) {
    const s = new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      displayedByDefault,
      config
    );

    s.items.forEach(item => {
      const correspondingItem = items.find(i => i.id === item.id);
      item.is_active_class = `active-icon-${correspondingItem.is_active_class}`;
      item.icon_name = correspondingItem.site_type ? `task-icon-${correspondingItem.site_type}` : '';
    });

    return s;
  }

  _tagSelector(selector, tagsGroup, fields, routingTagsUsesAi, company, metric) {
    let displayedByDefault = selector.displayed_by_default && tagsGroup.is_routing === 1; // Only the team tag can be displayed by default
    // XXX: there may be 2 team selectors in stats page, if it exists the one that should be displayed by default is "Team" (team value on action date)
    // while the other one "Team (message tag)" (team value now) has to be hidden.
    let tagGroupName = tagsGroup.name;

    if (tagsGroup.is_routing === 1) {
      if (selector.name) {
        tagGroupName = this.$translate.instant(`metrics/${selector.name}`);
      } else {
        if (selector.type === 'incoming-team-tags') {
          tagGroupName = this.$translate.instant('metrics/incoming-team-tag');
        } else {
          tagGroupName = this.$translate.instant('metrics/routing_tag_now');
          displayedByDefault = false;
        }
      }

      tagsGroup = this._addAiCategoriesForRoutingTag(selector, tagsGroup, routingTagsUsesAi);
    }

    if (tagsGroup.categories.length === 0) {
      const tags = tagsGroup.tags.map(t => ({ id: [t.id], name: t.name }));

      return new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
        fields,
        tagGroupName,
        tags,
        displayedByDefault
      );

      // return createEnumSelector(fields, tagGroupName, tags, displayedByDefault);
    } else {
      const items = [];
      let category = null;
      for (const i of tagsGroup.tags_and_categories) {
        if (i.is_category) {
          if (category !== null && category.subitems.length > 0) {
            items.push(category);
          }
          category = {
            name: i.name,
            subitems: [],
          };
        } else {
          if (category === null) {
            category = {
              name: '',
              subitems: [],
            };
          }
          category.subitems.push({
            id: [i.id],
            name: i.name,
          });
        }
      }

      if (category !== null && category.subitems.length > 0) {
        items.push(category);
      }

      return new HierarchicalEnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
        fields,
        tagGroupName,
        items,
        displayedByDefault
      );
    }
  }

  /**
   * Add AI categories for routing tags, if necessary
   */
  _addAiCategoriesForRoutingTag(selector, tagsGroup, routingTagsUsesAi) {
    if (selector.highlight_teams_with_ai) {
      const tagsGroupCopy = angular.copy(tagsGroup);

      tagsGroupCopy.categories = [
        {
          id: 0,
          name: this.$translate.instant('metrics/routing_tag_using_ai'),
        },
        {
          id: 1,
          name: this.$translate.instant('metrics/routing_tag_not_using_ai'),
        },
      ];

      const teamWithAiAgent = [
        { id: 0, name: this.$translate.instant('metrics/routing_tag_using_ai'), state: 'active', is_category: true },
      ];

      const teamWithoutAiAgent = [
        {
          id: 1,
          name: this.$translate.instant('metrics/routing_tag_not_using_ai'),
          state: 'active',
          is_category: true,
        },
      ];

      tagsGroupCopy.tags_and_categories.forEach(tagAndCat => {
        if (routingTagsUsesAi[tagAndCat.id]) {
          teamWithAiAgent.push(tagAndCat);
        } else {
          teamWithoutAiAgent.push(tagAndCat);
        }
      });

      tagsGroupCopy.tags_and_categories = [...teamWithAiAgent, ...teamWithoutAiAgent];
      return tagsGroupCopy;
    } else {
      return tagsGroup;
    }
  }

  _usersSelector(selector, name, users, company, metric) {
    const fields = selector.fields ? selector.fields : ['where:user'];
    const items = users.map(u => ({
      id: [u.id],
      name: u.realname,
    }));
    items.sort((a, b) => a.name.localeCompare(b.name));

    const s = new EnumSelector(company, metric, this.LocalStorageFactory, this.MetricsFactory).create(
      fields,
      name,
      items,
      selector.displayed_by_default
    );

    return [s];
  }

  static SelectorsFactory($translate, LocalStorageFactory, MetricsFactory, StrategiesFactory) {
    return new SelectorsService($translate, LocalStorageFactory, MetricsFactory, StrategiesFactory);
  }
}

SelectorsService.SelectorsFactory.$inject = [
  '$translate',
  'LocalStorageFactory',
  'MetricsFactory',
  'StrategiesFactory',
];
